import React from 'react';
import { createUseStyles } from 'react-jss';
import classNames from 'classnames';
import { Theme } from '@mui/material';
import { Link } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const useStyles = createUseStyles((theme: Theme) => {
    const { color, mq, motion } = theme.custom;
    return {
        root: {
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            marginBottom: 20,
            [mq.sm]: { gap: 12, marginBottom: 16 },
        },
        row: {
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 16,
            [mq.sm]: { gap: 12 },
        },
        titleBlock: { display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, flex: '1 1 320px' },
        back: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            fontSize: 13,
            fontWeight: 500,
            color: color.text.secondary,
            textDecoration: 'none',
            width: 'fit-content',
            minHeight: 24,
            borderRadius: 6,
            transitionProperty: 'color',
            transitionDuration: motion.fast,
            '&:hover': { color: color.primary.main },
            '& svg': { fontSize: 16 },
        },
        eyebrow: {
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: color.text.secondary,
        },
        titleRow: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10 },
        title: {
            margin: 0,
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            color: color.text.primary,
            overflowWrap: 'anywhere',
            [mq.sm]: { fontSize: 20 },
        },
        subtitle: { fontSize: 14, color: color.text.secondary, margin: 0 },
        meta: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '4px 14px',
            fontSize: 13,
            color: color.text.secondary,
            fontVariantNumeric: 'tabular-nums',
        },
        actions: {
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            marginLeft: 'auto',
            [mq.sm]: { width: '100%', marginLeft: 0, '& > *': { flex: '1 1 auto' } },
        },
        children: { minWidth: 0 },
    };
});

export interface PageHeaderProps {
    title: React.ReactNode;
    /** Small uppercase label above the title, e.g. the company or area name. */
    eyebrow?: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Chips next to the title. */
    badges?: React.ReactNode;
    /** Secondary facts under the title (ids, dates); each child becomes one item. */
    meta?: React.ReactNode;
    backTo?: { to: string; label: string };
    actions?: React.ReactNode;
    /** Tabs or a toolbar rendered under the title row. */
    children?: React.ReactNode;
    className?: string;
}

/** The title block of a page: back link, eyebrow, h1 + chips, meta line, actions on the right (stacked on phones). */
const PageHeader = ({ title, eyebrow, subtitle, badges, meta, backTo, actions, children, className }: PageHeaderProps) => {
    const classes = useStyles();
    return (
        <header className={classNames(classes.root, className)}>
            <div className={classes.row}>
                <div className={classes.titleBlock}>
                    {backTo && (
                        <Link to={backTo.to} className={classes.back}>
                            <ArrowBackIcon aria-hidden="true" />
                            {backTo.label}
                        </Link>
                    )}
                    {eyebrow && <span className={classes.eyebrow}>{eyebrow}</span>}
                    <div className={classes.titleRow}>
                        <h1 className={classes.title}>{title}</h1>
                        {badges}
                    </div>
                    {subtitle && <p className={classes.subtitle}>{subtitle}</p>}
                    {meta && <div className={classes.meta}>{meta}</div>}
                </div>
                {actions && <div className={classes.actions}>{actions}</div>}
            </div>
            {children && <div className={classes.children}>{children}</div>}
        </header>
    );
};

export default PageHeader;
